import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { IParcelStore } from "dto/Mailroom/IMailroom";
import { IRequestCreateOutGoingParcel } from "./parcelService";

/** Status wewnętrzny obiegu pisma (odpowiednik BE DocumentInternalStatus). */
export enum EDocumentInternalStatus {
  Project = 0,
  GeneratedLetters = 4,
  ReadyToSign = 5,
  Sent = 7,
  ReadyToDispatch = 29,
  ReadyToDigitalSign = 30,
  SignedInternal = 31,
}

export interface IPrepareLettersResponse {
  HasCase: boolean;
  DefaultStoreId: string | null;
  Stores: IParcelStore[];
  InternalStatus: EDocumentInternalStatus;
  HasSignedVersion: boolean;
  /**
   * Plik pisma BAZOWEGO (projektu) do podglądu przy pozycjonowaniu znacznika
   * własnego podpisu — `SignedPdfUri ?? FileUri` dokumentu. Gdy to nie-PDF (docx),
   * podgląd renderuje wtyczka. Klucz i nazwa idą w parze, by odszyfrować/wyświetlić.
   * (BE: uzupełnić w handlerze prepare z encji EditorDocument.)
   */
  BaseFileUri?: string | null;
  BaseFileName?: string | null;
  BaseEncryptionKeyId?: string | null;
}

export interface IPassToSignRequest {
  SignerUserId: string;
  Note?: string | null;
  IsImportant?: boolean;
  DocumentEncryptionKeyId?: string | null;
  /** Klucz obiektu dokumentu zaszyfrowany kluczem publicznym podpisującego (base64). */
  ReWrappedDocumentKey?: string | null;
  /** Wybrani adresaci + skład + data (bez kluczy odbiorców). */
  Generation: IRequestCreateOutGoingParcel;
}

export interface ICompleteSignRequest {
  Generation: IRequestCreateOutGoingParcel;
}

export interface IPendingLettersResponse {
  DocumentId: string;
  AuthorUserId: string;
  Generation: IRequestCreateOutGoingParcel;
}

// --- Krok „prepare content": BE wylicza per odbiorca mapę zmiennych DOCVARIABLE ---

export interface IPrepareContentReceiver {
  ReceiverId: string;
  Id: string | null;
  /** Typ kontaktu (ContactType — zgodny wartościowo z EEntityType używanym przy odbiorcach). */
  Type: number;
  Email?: string;
  Cc: boolean;
}

export interface IPrepareContentRequest {
  DocumentId: string;
  DocumentDate?: string | null;
  Receivers: IPrepareContentReceiver[];
}

export interface IPreparedReceiver {
  ReceiverId: string;
  Variables: Record<string, string>;
  RemoveParagraphsForVariables: string[];
}

export interface IPrepareContentResponse {
  Receivers: IPreparedReceiver[];
}

const prepareContent = (
  payload: IPrepareContentRequest,
): Promise<{ data: IPrepareContentResponse }> =>
  axiosInstance.post("outgoingParcel/generateLetters/content", payload);

const usePrepareContent = () => useCustomMutation(prepareContent);

const generateLettersQueryKeys = {
  prepare: (documentId: string) => ["generateLetters.prepare", documentId],
  pending: (documentId: string) => ["generateLetters.pending", documentId],
};

const prepare = (
  documentId: string,
): Promise<{ data: IPrepareLettersResponse }> =>
  axiosInstance.get(`outgoingParcel/generateLetters/prepare/${documentId}`);

const usePrepareLetters = (documentId: string) =>
  useCustomQuery(
    generateLettersQueryKeys.prepare(documentId),
    () => prepare(documentId),
    { enabled: !!documentId },
  );

const setReadyToSign = (documentId: string): Promise<{ data: unknown }> =>
  axiosInstance.post(`outgoingParcel/generateLetters/setReadyToSign/${documentId}`);

const useSetReadyToSign = () => useCustomMutation(setReadyToSign);

const passToSign = (
  payload: IPassToSignRequest,
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/generateLetters/passToSign", payload);

const usePassToSign = () => useCustomMutation(passToSign);

const getPendingBatch = (
  documentId: string,
): Promise<{ data: IPendingLettersResponse }> =>
  axiosInstance.get(`outgoingParcel/generateLetters/pending/${documentId}`);

const useGetPendingBatch = (documentId: string) =>
  useCustomQuery(
    generateLettersQueryKeys.pending(documentId),
    () => getPendingBatch(documentId),
    { enabled: !!documentId },
  );

const completeSign = (
  payload: ICompleteSignRequest,
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/generateLetters/completeSign", payload);

const useCompleteSign = () => useCustomMutation(completeSign);

export default {
  generateLettersQueryKeys,
  usePrepareLetters,
  usePrepareContent,
  // Surowa funkcja do imperatywnego wywołania w pętli generacji pism.
  prepareContent,
  useSetReadyToSign,
  usePassToSign,
  useGetPendingBatch,
  useCompleteSign,
  // Surowe funkcje do imperatywnego wywołania (np. po podpisie w CertificateApi).
  getPendingBatch,
  completeSign,
};
